import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { axiosInstance } from "../../api/api";
import { Routes } from "../../routes";

interface User {
  id: number;
  userId?: number;
  name: string;
}

interface LocationState {
  groupName: string;
  patrolCount: number;
}

export const AssignUsersToGroup: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;

  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [assignedUsers, setAssignedUsers] = useState<User[]>([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchSecurityUsers = async () => {
    try {
      setFetchLoading(true);
      const res = await axiosInstance.get("/api/ap/v1/employee", {
        params: { page: 1, per_page: 100, role: "security" },
      });

      const userData = (res.data?.data || []).map((u: any) => ({
        id: u.user_id,
        name: `${u.employee_personal?.full_name || "Unknown"} (${u.user_id})`,
      }));

      setUsers(userData);
    } catch (err: any) {
      console.error("❌ Error fetching users:", err);
      setError("Gagal memuat data user security.");
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchAssignedUsers = async () => {
    if (!groupId) return;
    try {
      const res = await axiosInstance.get(
        `/api/v1/patrol/group/component/${groupId}/index`
      );

      const assignedData = (res.data?.data || []).map((item: any) => ({
        id: item.id,
        userId: item.user?.id || item.user_id,
        name:
          item.user?.employee_personal?.full_name
            ? `${item.user.employee_personal.full_name} (${item.user.id})`
            : item.user?.name
            ? `${item.user.name} (${item.user.id})`
            : `User ${item.user_id}`,
      }));

      setAssignedUsers(assignedData);
    } catch (err) {
      console.error("❌ Error fetching assigned users:", err);
    }
  };

  useEffect(() => {
    if (groupId) {
      fetchSecurityUsers();
      fetchAssignedUsers();
    }
  }, [groupId]);

  const handleUserSelect = (userId: number) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((user) => user.id));
    }
  };

  const assignUsersToGroup = async () => {
    if (!groupId || selectedUsers.length === 0) return;

    setAssigning(true);
    setError("");
    setSuccess("");

    const results: any[] = [];

    try {
      for (const userId of selectedUsers) {
        try {
          const payload = { user_id: userId };
          await axiosInstance.post(
            `/api/v1/patrol/group/component/${groupId}/store`,
            payload
          );
          results.push({ userId, success: true });
        } catch (err: any) {
          const msg =
            err.response?.data?.message ||
            `Gagal assign user ${userId} (mungkin sudah diassign)`;
          results.push({ userId, success: false, error: msg });
          console.error(`❌ Failed assign user ${userId}:`, msg);
        }
        await new Promise((r) => setTimeout(r, 200));
      }

      const successCount = results.filter((r) => r.success).length;
      const failCount = results.length - successCount;

      if (failCount === 0) {
        setSuccess(`✅ Berhasil assign ${successCount} user ke group.`);
      } else if (successCount > 0) {
        const failMessages = results
          .filter((r) => !r.success)
          .map((r) => r.error)
          .join(", ");
        setSuccess(
          `Sebagian berhasil (${successCount} sukses, ${failCount} gagal).`
        );
        setError(failMessages);
      } else {
        const failMessages = results.map((r) => r.error).join(", ");
        setError(failMessages);
      }

      setSelectedUsers([]);
      fetchAssignedUsers();
    } catch (err) {
      console.error("🔥 Unexpected error:", err);
      setError("Terjadi kesalahan saat assign user ke group.");
    } finally {
      setAssigning(false);
    }
  };

  const removeUserFromGroup = async (componentId: number, userName: string) => {
    if (!groupId) return;
    const confirmDelete = window.confirm(
      `Yakin ingin menghapus ${userName} dari group?`
    );
    if (!confirmDelete) return;

    try {
      await axiosInstance.delete(
        `/api/v1/patrol/group/component/${groupId}/destroy/${componentId}`
      );
      setAssignedUsers((prev) => prev.filter((u) => u.id !== componentId));
    } catch (err) {
      console.error("❌ Gagal hapus user dari group:", err);
    }
  };

  if (fetchLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <div>Loading users...</div>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: "1000px",
        margin: "0 auto",
        height: "calc(100vh - 40px)",
        display: "flex",
        flexDirection: "column",
        padding: "20px",
      }}
    >
      {/* Header */}
      <div style={{ flex: "0 0 auto" }}>
        <div
          style={{
            backgroundColor: "#e3f2fd",
            padding: "16px",
            borderRadius: "4px",
            marginBottom: "20px",
          }}
        >
          <h1 style={{ margin: "0 0 12px 0" }}>Assign Users ke Group</h1>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
              fontSize: "14px",
            }}
          >
            <div>
              <strong>Group Name:</strong> {locationState?.groupName || "N/A"}
            </div>
            <div>
              <strong>Group ID:</strong> {groupId}
            </div>
          </div>
        </div>
      </div>


      <div style={{ flex: "1 1 auto", overflowY: "auto", paddingRight: "8px" }}>
        {/* Error / Success Messages */}
        {error && (
          <div
            style={{
              backgroundColor: "#ffebee",
              border: "1px solid #f44336",
              color: "#c62828",
              padding: "12px",
              borderRadius: "4px",
              marginBottom: "20px",
            }}
          >
            {error}
          </div>
        )}
        {success && (
          <div
            style={{
              backgroundColor: "#e8f5e9",
              border: "1px solid #4caf50",
              color: "#2e7d32",
              padding: "12px",
              borderRadius: "4px",
              marginBottom: "20px",
            }}
          >
            {success}
          </div>
        )}

        {/* User List */}
        <div
          style={{
            border: "1px solid #ddd",
            borderRadius: "4px",
            padding: "16px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <h2 style={{ margin: 0 }}>
              Pilih User Security ({selectedUsers.length} terpilih)
            </h2>
            <button
              type="button"
              onClick={handleSelectAll}
              style={{
                padding: "6px 12px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: "white",
                cursor: "pointer",
              }}
            >
              {selectedUsers.length === users.length
                ? "Batal Pilih Semua"
                : "Pilih Semua"}
            </button>
          </div>

          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {users.length === 0 ? (
              <p
                style={{ textAlign: "center", color: "#666", padding: "20px" }}
              >
                Tidak ada data user security
              </p>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px",
                }}
              >
                {users.map((user) => (
                  <div
                    key={user.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "8px",
                      backgroundColor: selectedUsers.includes(user.id)
                        ? "#f0f8ff"
                        : "transparent",
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`user-${user.id}`}
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleUserSelect(user.id)}
                      style={{ marginRight: "8px" }}
                    />
                    <label htmlFor={`user-${user.id}`} style={{ flex: 1 }}>
                      {user.name}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Assigned Users */}
        <div
          style={{
            border: "1px solid #ddd",
            borderRadius: "4px",
            padding: "16px",
            backgroundColor: "#fafafa",
            marginBottom: "24px",
          }}
        >
          <h2 style={{ marginTop: 0 }}>
            User yang sudah diassign ({assignedUsers.length})
          </h2>
          <div
            style={{
              maxHeight: "200px",
              overflowY: assignedUsers.length > 4 ? "auto" : "visible",
              borderTop: "1px solid #ccc",
              marginTop: "8px",
              paddingTop: "8px",
            }}
          >
            {assignedUsers.length === 0 ? (
              <p style={{ color: "#777", textAlign: "center" }}>
                Belum ada user yang diassign ke group ini.
              </p>
            ) : (
              assignedUsers.map((user) => (
                <div
                  key={user.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 0",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <span>{user.name}</span>
                  <button
                    onClick={() =>
                      removeUserFromGroup(user.id, user.name)
                    }
                    style={{
                      backgroundColor: "#ff4d4f",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      cursor: "pointer",
                    }}
                  >
                    Hapus
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div
        style={{
          flex: "0 0 auto",
          display: "flex",
          justifyContent: "space-between",
          gap: "12px",
          marginTop: "24px",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          disabled={assigning}
          style={{
            padding: "10px 20px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            backgroundColor: "white",
            cursor: assigning ? "not-allowed" : "pointer",
            opacity: assigning ? 0.6 : 1,
          }}
        >
          Kembali
        </button>

        <div style={{ display: "flex", gap: "12px" }}>
          <button
            onClick={() => navigate(Routes.GroupSpace)}
            style={{
              padding: "10px 20px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              backgroundColor: "white",
              cursor: "pointer",
            }}
          >
            Lihat Semua Group
          </button>

          <button
            onClick={assignUsersToGroup}
            disabled={assigning || selectedUsers.length === 0}
            style={{
              padding: "10px 20px",
              backgroundColor:
                assigning || selectedUsers.length === 0 ? "#ccc" : "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor:
                assigning || selectedUsers.length === 0
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {assigning
              ? `Assigning ${selectedUsers.length} Users...`
              : `Assign ${selectedUsers.length} Users`}
          </button>
        </div>
      </div>
    </div>
  );
};
